import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import Dropdown from '../../common/Dropdown'
import SimpleTextInput from '../../common/SimpleTextInput'
import ToggleSwitch from '../../common/ToggleSwitch'
import TooltipedIcon from '../../common/TooltipedIcon'
import { toast } from '../../common/Toast'
import { productModelsWritePermissions } from '../../../utils/permissionValidation'
import { capitalizeFirstLetter } from '../../../utils/functions'
import { InformationIcon } from '../../../icons'
import { clearProductModelsMessages, editProductModelsVariants, createProductModelsVariants } from '../../../actions/productModels'
import '../uploads.scss'

const boolFromAnything = (v) => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return ['true', '1', 'y', 'yes'].includes(v.toLowerCase())
  if (typeof v === 'number') return v === 1
  return false
}

const ynToBool = (v) =>
  String(v || '')
    .trim()
    .toUpperCase() === 'Y'
const boolToYN = (b) => (b ? 'Y' : 'N')

const AddEditProductModelsVariants = (props) => {
  const { t } = useTranslation()
  const { modelDetails, closeModal, showSpinner, setShowSpinner } = props
  const isEditMode = !!modelDetails?.id
  const STATUSES = props.modelStatuses
  const MODEL_CATEGORIES = props.modelCategories

  const modelOptions = useMemo(() => {
    return (props.models || [])
      .filter((m) => m?.isActive)
      .map((m) => ({
        key: m?.id ?? '',
        value: (m?.model ?? '').toString(),
      }))
      .filter((o) => o.key && o.value)
  }, [props.models])

  const deviceModelFromResponse = modelDetails?.deviceModel || null
  const [deviceModelId, setDeviceModelId] = useState(deviceModelFromResponse?.id ?? '')
  const [status, setStatus] = useState(modelDetails?.status ?? 'active')
  const [modelNumber, setModelNumber] = useState(modelDetails?.modelNumber ?? '')
  const [productModelName, setProductModelName] = useState(modelDetails?.productModelName ?? '')
  const [modelCategories, setModelCategories] = useState(modelDetails?.modelCategory ?? '')
  const [serialNumberPrefix, setSerialNumberPrefix] = useState(modelDetails?.serialNumberPrefix ?? '')
  const [availableToCountry, setAvailableToCountry] = useState(modelDetails?.availableToCountry ?? '')
  const [voltage, setVoltage] = useState(modelDetails?.voltage ?? '')
  const [powerCord, setPowerCord] = useState(modelDetails?.powerCord ?? '')
  const [currentStatusMhAvailable, setCurrentStatusMhAvailable] = useState(boolFromAnything(modelDetails?.currentStatusMhAvailable))
  const [ce, setCe] = useState(ynToBool(modelDetails?.ce ?? ''))
  const [tuv, setTuv] = useState(ynToBool(modelDetails?.tuv ?? ''))
  const [vci, setVci] = useState(ynToBool(modelDetails?.vci ?? ''))
  const [en13060, setEn13060] = useState(ynToBool(modelDetails?.en13060 ?? ''))
  const [recyclable, setRecyclable] = useState(ynToBool(modelDetails?.recyclable ?? ''))
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const boolStatuses = ['no', 'yes']

  useEffect(() => {
    if (!modelDetails) {
      setDeviceModelId('')
      setStatus('inactive')
      setModelNumber('')
      setProductModelName('')
      setModelCategories('')
      setSerialNumberPrefix('')
      setAvailableToCountry('')
      setVoltage('')
      setPowerCord('')
      setCurrentStatusMhAvailable(false)
      setCe('')
      setTuv('')
      setVci('')
      setEn13060('')
      setRecyclable('')
      setErrors({})
      return
    }

    setDeviceModelId(modelDetails?.deviceModel?.id ?? '')
    setStatus(modelDetails?.status ?? 'active')
    setModelNumber(modelDetails?.modelNumber ?? '')
    setProductModelName(modelDetails?.productModelName ?? '')
    setModelCategories(modelDetails?.modelCategory ?? '')
    setSerialNumberPrefix(modelDetails?.serialNumberPrefix ?? '')
    setAvailableToCountry(modelDetails?.availableToCountry ?? '')
    setVoltage(modelDetails?.voltage ?? '')
    setPowerCord(modelDetails?.powerCord ?? '')
    setCurrentStatusMhAvailable(boolFromAnything(modelDetails?.currentStatusMhAvailable))
    setCe(ynToBool(modelDetails?.ce ?? ''))
    setTuv(ynToBool(modelDetails?.tuv ?? ''))
    setVci(ynToBool(modelDetails?.vci ?? ''))
    setEn13060(ynToBool(modelDetails?.en13060 ?? ''))
    setRecyclable(ynToBool(modelDetails?.recyclable ?? ''))
    setErrors({})
  }, [modelDetails?.id])

  const validate = () => {
    const next = {}
    if (!deviceModelId) next.deviceModelId = true
    if (!status) next.status = true
    if (!modelNumber?.trim()) next.modelNumber = true
    if (!productModelName?.trim()) next.productModelName = true
    if (!modelCategories?.trim()) next.modelCategories = true
    if (!serialNumberPrefix?.trim()) next.serialNumberPrefix = true
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildPayload = () => ({
    status,
    modelNumber: modelNumber.trim(),
    productModelName: productModelName.trim(),
    modelCategory: modelCategories ?? '',
    serialNumberPrefix: serialNumberPrefix ?? '',
    availableToCountry: availableToCountry ?? '',
    voltage: voltage ?? '',
    powerCord: powerCord ?? '',
    currentStatusMhAvailable,
    ce: boolToYN(ce) ?? '',
    tuv: boolToYN(tuv) ?? '',
    vci: boolToYN(vci) ?? '',
    en13060: boolToYN(en13060) ?? '',
    recyclable: boolToYN(recyclable) ?? '',
  })

  const handleSave = async () => {
    if (!validate()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const payload = buildPayload()
    setSaving(true)
    setShowSpinner?.(true)

    let data = null

    try {
      if (isEditMode) {
        data = await props.actions.editProductModelsVariants(modelDetails?.deviceModel?.id, modelDetails.id, payload)
      } else {
        data = await props.actions.createProductModelsVariants(deviceModelId, payload)
      }
      closeModal()

      if (data?.code) {
        toast.error(t(`error.${data?.code}`))
        return
      }
    } catch (error) {
      if (error?.name) {
        toast.error(t(`error.${error.name}`))
        return
      }
      toast.error(t('error.something_went_wrong'))
    } finally {
      setSaving(false)
      setShowSpinner?.(false)
      props.actions.clearProductModelsMessages?.()
    }
  }

  const headerContent = () => (
    <div className="status-wrapper">
      <div className="label d-flex flex-justify-start flex-align-center">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.variant_status_info')}`} orientation="bottom" />
        {t('status')}
      </div>
      <Dropdown
        className="dropdown-input"
        options={STATUSES.map((s) => ({
          key: s,
          value: t(s),
        }))}
        initialSelectedKey={status}
        onChange={(opt) => setStatus(opt.key)}
        hasError={errors.status}
      />
    </div>
  )

  return (
    <PageModal
      toggle
      onToggle={closeModal}
      className={`uploads-modal variants-wrapper ${deviceModelId ? '' : 'half-modal'}`}
      title={isEditMode ? t('edit') : t('product_models_context.add_new_variant')}
      headerContent={headerContent()}
      footerContent={
        <>
          <SimpleButton className="cancel-button" onClick={closeModal}>
            {t('cancel')}
          </SimpleButton>
          <SimpleButton className="submit-button" onClick={handleSave} disabled={saving || !!showSpinner} requiredPermission={productModelsWritePermissions}>
            {saving ? t('saving') : t('save')}
          </SimpleButton>
        </>
      }
    >
      <div className="uploads-modal-content">
        <div className="section-title">{t('product_models_context.general_model_details').toUpperCase()}</div>
        {!isEditMode && (
          <div className="row-wrapper">
            <div className="input-wrapper">
              <div className="label d-flex flex-justify-start flex-align-center">
                <p className="input-option flex-justify-start">{`${t('model')}*`}</p>
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.model_status_info')}`} orientation="right" />
              </div>
              <Dropdown
                className="dropdown-input"
                placeholder={t('please_select')}
                options={modelOptions}
                initialSelectedKey={deviceModelId || undefined}
                onChange={(opt) => setDeviceModelId(opt.key)}
                hasError={errors.deviceModelId}
              />
            </div>
            <div className="input-wrapper"></div>
          </div>
        )}
        {isEditMode && (
          <>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option flex-justify-start">{t('model')}</p>
                <div className="value">{deviceModelFromResponse?.name || '-'}</div>
              </div>
              <div className="input-wrapper">
                <p className="input-option">{t('product_type')}</p>
                <div className="value">{deviceModelFromResponse?.productType || '-'}</div>
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('category')}</p>
                <div className="value">{deviceModelFromResponse?.category || '-'}</div>
              </div>
              <div className="input-wrapper">
                <p className="input-option">{t('dealer-portal.brand')}</p>
                <div className="value">{deviceModelFromResponse?.brand || '-'}</div>
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.device_function')}</p>
                <div className="value">{deviceModelFromResponse?.deviceFunction || '-'}</div>
              </div>
            </div>
          </>
        )}
        {deviceModelId && (
          <>
            <div className="separator" />
            <div className="section-title">{t('product_models_context.model_variant_details').toUpperCase()}</div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{`${t('device_context.model_number')}*`}</p>
                <SimpleTextInput value={modelNumber} onChange={(v) => setModelNumber(v)} onClear={() => setModelNumber('')} hasError={errors.modelNumber} />
              </div>
              <div className="input-wrapper">
                <p className="input-option">{`${t('product_models_context.product_model_name')}*`}</p>
                <SimpleTextInput
                  value={productModelName}
                  onChange={(v) => setProductModelName(v)}
                  onClear={() => setProductModelName('')}
                  hasError={errors.productModelName}
                />
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{`${t('product_models_context.product_suffix')}*`}</p>
                <Dropdown
                  className="dropdown-input"
                  placeholder={t('please_select')}
                  options={MODEL_CATEGORIES.map((mc) => ({
                    key: mc,
                    value: capitalizeFirstLetter(mc),
                  }))}
                  initialSelectedKey={modelCategories}
                  onChange={(opt) => setModelCategories(opt.key)}
                  hasError={errors.modelCategories}
                />
              </div>
              <div className="input-wrapper">
                <p className="input-option">{`${t('product_models_context.serial_number_prefix')}*`}</p>
                <SimpleTextInput
                  value={serialNumberPrefix}
                  onChange={(v) => setSerialNumberPrefix(v)}
                  onClear={() => setSerialNumberPrefix('')}
                  hasError={errors.serialNumberPrefix}
                />
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.power_cord')}</p>
                <SimpleTextInput value={powerCord} onChange={(v) => setPowerCord(v)} onClear={() => setPowerCord('')} />
              </div>
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.voltage')}</p>
                <SimpleTextInput value={voltage} onChange={(v) => setVoltage(v)} onClear={() => setVoltage('')} />
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.mh_available')}</p>
                <div className="d-flex gap-8">
                  <ToggleSwitch
                    checked={boolFromAnything(modelDetails?.currentStatusMhAvailable)}
                    onChange={() => setCurrentStatusMhAvailable((prevValue) => !prevValue)}
                    isDisabled={showSpinner}
                  />
                  <span className="status">{t(boolStatuses[Number(boolFromAnything(currentStatusMhAvailable))])}</span>
                </div>
              </div>
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.product_ce')}</p>
                <div className="d-flex gap-8">
                  <ToggleSwitch checked={ce} onChange={() => setCe((prevValue) => !prevValue)} isDisabled={showSpinner} />
                  <span className="status">{t(boolStatuses[Number(boolFromAnything(ce))])}</span>
                </div>
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.product_vci')}</p>
                <div className="d-flex gap-8">
                  <ToggleSwitch checked={vci} onChange={() => setVci((prevValue) => !prevValue)} isDisabled={showSpinner} />
                  <span className="status">{t(boolStatuses[Number(boolFromAnything(vci))])}</span>
                </div>
              </div>
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.product_tuv')}</p>
                <div className="d-flex gap-8">
                  <ToggleSwitch checked={tuv} onChange={() => setTuv((prevValue) => !prevValue)} isDisabled={showSpinner} />
                  <span className="status">{t(boolStatuses[Number(boolFromAnything(tuv))])}</span>
                </div>
              </div>
            </div>
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.product_en13060')}</p>
                <div className="d-flex gap-8">
                  <ToggleSwitch checked={en13060} onChange={() => setEn13060((prevValue) => !prevValue)} isDisabled={showSpinner} />
                  <span className="status">{t(boolStatuses[Number(boolFromAnything(en13060))])}</span>
                </div>
              </div>
              <div className="input-wrapper">
                <p className="input-option">{t('product_models_context.product_recyclable')}</p>
                <div className="d-flex gap-8">
                  <ToggleSwitch checked={recyclable} onChange={() => setRecyclable((prevValue) => !prevValue)} isDisabled={showSpinner} />
                  <span className="status">{t(boolStatuses[Number(boolFromAnything(recyclable))])}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </PageModal>
  )
}

function stateToProps({ productModels }) {
  return {
    models: productModels?.productModelsFilterOptions?.models || [],
    modelCategories: productModels?.productModelsFilterOptions?.modelCategory || [],
    modelStatuses: productModels?.productModelsFilterOptions?.status || [],
    countries: productModels?.productModelsFilterOptions?.countries || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createProductModelsVariants,
        editProductModelsVariants,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditProductModelsVariants)

